import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/20/solid";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { useSignIn } from "@clerk/clerk-react";
import clsx from "clsx";
import { routeEndpoints } from "../../routes/routeEndpoints";
import { clerkErrorMessage } from "../../utils/clerkError";

const INCOMPLETE_RESET_MESSAGE =
  "We couldn't reset your password. Please start over.";

type ResetPasswordFormValues = {
  password: string;
  confirmPassword: string;
};

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { isLoaded, signIn, setActive } = useSignIn();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasReset, setHasReset] = useState(false);
  const userEmail = signIn?.identifier || "";
  const {
    register,
    handleSubmit,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>();

  const onSubmit = async (data: ResetPasswordFormValues) => {
    if (!isLoaded) return;

    try {
      const result = await signIn.resetPassword({ password: data.password });

      if (result.status !== "complete") {
        setError("root", { message: INCOMPLETE_RESET_MESSAGE });
        return;
      }

      setHasReset(true);
      await setActive({ session: result.createdSessionId });
      navigate(routeEndpoints.orders, { replace: true });
    } catch (error) {
      setError("root", { message: clerkErrorMessage(error) });
    }
  };

  if (isLoaded && !hasReset && signIn.status !== "needs_new_password") {
    return <Navigate to={routeEndpoints.forgotPassword} replace />;
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col font-sans">
      <div className="w-full max-w-xl md:ml-28 mt-9 px-4 md:px-0">
        <button
          type="button"
          onClick={() => navigate(routeEndpoints.login)}
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors font-medium cursor-pointer"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to Login
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-xl bg-white rounded-xl shadow-sm border border-slate-100 md:px-10 md:py-9 px-6 py-8">
          <div className="flex flex-col gap-8">
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center justify-center size-14 rounded-full bg-blue-50">
                <LockClosedIcon className="size-6 text-blue-600" />
              </div>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="md:text-2xl md:font-semibold text-xl font-medium text-gray-950">
                  Create new password
                </h1>
                <p className="text-sm md:font-medium font-normal text-gray-400">
                  Set a new password for your account.
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-8"
            >
              {/* Hidden input for Username */}
              <input
                type="email"
                name="username"
                defaultValue={userEmail}
                autoComplete="username"
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                className="absolute opacity-0 w-0 h-0 pointer-events-none -z-10"
              />

              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="password"
                    className="text-sm font-medium text-gray-950"
                  >
                    New password
                  </label>
                  <div
                    className={clsx(
                      "flex items-center gap-2 rounded-xl border px-4 py-3.5 focus-within:ring-2",
                      errors.password
                        ? "border-red-500 focus-within:ring-red-500"
                        : "border-gray-200 focus-within:ring-blue-500",
                    )}
                  >
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Create a strong password"
                      className="flex-1 min-w-0 text-sm outline-none placeholder:text-gray-400"
                      {...register("password", {
                        required: "Password is required",
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="text-gray-400 hover:text-gray-500 cursor-pointer"
                    >
                      {showPassword ? (
                        <EyeIcon className="size-5" />
                      ) : (
                        <EyeSlashIcon className="size-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-500">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="confirmPassword"
                    className="text-sm font-medium text-gray-950"
                  >
                    Confirm password
                  </label>
                  <div
                    className={clsx(
                      "flex items-center gap-2 rounded-xl border px-4 py-3.5 focus-within:ring-2",
                      errors.confirmPassword
                        ? "border-red-500 focus-within:ring-red-500"
                        : "border-gray-200 focus-within:ring-blue-500",
                    )}
                  >
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Confirm your password"
                      className="flex-1 min-w-0 text-sm outline-none placeholder:text-gray-400"
                      {...register("confirmPassword", {
                        required: "Please confirm your password",
                        validate: (value) =>
                          value === getValues("password") ||
                          "Passwords do not match",
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="text-gray-400 hover:text-gray-500 cursor-pointer"
                    >
                      {showConfirmPassword ? (
                        <EyeIcon className="size-5" />
                      ) : (
                        <EyeSlashIcon className="size-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-500">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-col">
                {errors.root && (
                  <p className="mb-4 text-sm text-center text-red-500">
                    {errors.root.message}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!isLoaded || isSubmitting}
                  className="w-full h-13 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Resetting…" : "Reset password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
